/* eslint-disable indent */
import { styled } from '@mui/material/styles';
import { i18n } from 'i18n';
import { usePage } from 'providers/page';
import React from 'react';
import { UseQueryResult } from 'react-query';
import {
  useLocation,
  useParams
} from 'react-router-dom';

const PREFIX = 'makePage';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('section')((
  {
    theme,
  },
) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(3),
    width: '100%',
  },
}));

export interface BasePageProps<TQueries extends UseQueryResult[] = []> {
  queries: [...TQueries];
}
export interface makePageConfig<TParams, TQueries extends UseQueryResult[] = [], TParentQueries extends UseQueryResult[] = []> {
  WrappedPage?: React.ComponentType<BasePageProps<[...TParentQueries, ...TQueries]>>;
  titleFn?: (data: [...TParentQueries, ...TQueries]) => string;
  inheritParent?: boolean;
  parentPathTemplate?: string;
  parentTitleFn?: (data: [...TParentQueries, ...TQueries]) => string;
  pageDataHook?: (params: TParams) => TQueries;
}

const resolveParentPath = (actualPath: string, parentPathTemplate?: string) => {
  if (!parentPathTemplate) {
    return null;
  }

  if (actualPath.startsWith(parentPathTemplate)) {
    return parentPathTemplate;
  }

  const actualPathParts = actualPath.split('/');
  return parentPathTemplate
    .split('/')
    .map((part, index) => (part.includes(':') ? actualPathParts[index] : part))
    .join('/');
};

export const pathMatchesWithPathTemplate = (pathToMatch: string, pathTemplate: string) => {
  const pathToMatchParts = pathToMatch.split('/');
  const pathTemplateParts = pathTemplate.split('/');

  if (pathToMatchParts.length < pathTemplateParts.length) {
    return false;
  }

  const pathToMatchPartsRelativeToTemplate = pathToMatchParts.slice(0, pathTemplateParts.length);

  return pathToMatchPartsRelativeToTemplate.every((part, index) => pathTemplateParts[index].includes(':')
    || pathTemplateParts[index] === '*'
    || part === pathTemplateParts[index]);
};

export interface MakePageProps<TParentQueries extends UseQueryResult[] = []> {
  parentQueries?: TParentQueries;
}

/* eslint-disable indent */
const makePage = <TParams, TQueries extends UseQueryResult[] = [], TParentQueries extends UseQueryResult[] = []>({
  WrappedPage,
  titleFn,
  inheritParent = false,
  parentTitleFn,
  parentPathTemplate,
  pageDataHook,
}: makePageConfig<TParams, TQueries, TParentQueries>) => ({ parentQueries = [] as unknown as TParentQueries }: MakePageProps<TParentQueries>) => {
  const { actions } = usePage();
  const { pathname, state } = useLocation();
  const params = useParams<any>();

  const pageQueries = pageDataHook ? pageDataHook(params as TParams) : [] as unknown as TQueries;
  const queries: [...TParentQueries, ...TQueries] = [...parentQueries, ...pageQueries];

  // A caller can override the back-navigation target by passing { parentPath, parentTitle }
  // in the router location state (e.g. so the dashboard's edit page returns to the overview
  // the user came from rather than the static parent route).
  const locationState = state as { parentPath?: string; parentTitle?: string } | null;

  const title = titleFn ? titleFn(queries) : undefined;
  const parentTitle = locationState?.parentTitle ?? (parentTitleFn ? i18n._(parentTitleFn(queries)) : undefined);
  const resolvedParentPath = locationState?.parentPath ?? resolveParentPath(pathname, parentPathTemplate);

  React.useEffect(() => {
    !inheritParent && actions.setParentPath(resolvedParentPath ?? null);
    !inheritParent && actions.setParentTitle(parentTitle ?? null);
    title !== undefined && actions.setTitle(title);
  }, [actions, resolvedParentPath, title, parentTitle]);

  if (!WrappedPage) {
    return <></>;
  }

  return (
    <Root className={classes.root}>
      <WrappedPage queries={queries} />
    </Root>
  );
};
/* eslint-enable indent */

export default makePage;
